import intersection from 'hs-lodash/intersection';
import getLangLocale from 'I18n/utils/getLangLocale';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Elements } from 'react-stripe-js';
import { Grid, GridItem } from 'foundations-components/transitional/components/Grid';
import OrderSummary from '../components/OrderSummary/OrderSummary';
import PaymentStatus from '../components/PaymentInfo/PaymentStatus';
import TestModeAlert from '../components/Utilities/TestModeAlert';
import PaymentContainer from './PaymentContainer';
import MeetingTime from '../components/OrderSummary/MeetingTime';
import OrderDescription from '../components/OrderSummary/OrderDescription';
import { AUTO, BYO_STRIPE, ELEMENTS_APPEARANCE, ELEMENTS_FONTS, FC_VERIFICATION_METHOD, HS_PAYMENTS, OFF_SESSION, PAYMENT, SETUP } from '../constants/PaymentElement';
import { ACH, BACS, CARD, PADS, SEPA } from '../constants/PaymentMethods';
import { CANCELED, OPEN } from '../constants/Statuses';
import useEscapeListener from '../hooks/useEscapeListener';
import { useFireAlarm } from '../hooks/useFireAlarm';
import { useTransactionLimits } from '../hooks/useTransactionLimits';
import { getValidPaymentMethods, hsToStripePaymentMethod } from '../utils/paymentMethods';
import { getIframeLayout, LAYOUTS } from '../utils/queryParams';
import { getStripePromise } from '../utils/stripe';
import { initializeSubscriptionTracker } from '../utils/subscriptionTracking';
import { getPageInteractionSections, getSalesTaxType, initializePaymentsTracker, PAYMENT_SCHEDULE_MARKER, track } from '../utils/tracking';
import { getIsIframePreviewTestUrl, getIsIframePreviewUrl } from '../utils/urls';
import { getIsZeroCheckoutValid } from '../utils/validations';
import HubSpotTermsOfServiceFooter from '../components/HubSpotTermsOfServiceFooter';
import { usePaymentFormContext } from '../context/PaymentFormContext';
import FullPageError from '../components/Utilities/FullPageError';
import { View } from 'foundations-components/components/View';
import { useTheme } from 'foundations-theming/utils';
import unescapedText from 'I18n/utils/unescapedText';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FIRE_ALARM_CONTAINER_ID = 'fire-alarm-container';
const STATUS_SUBSCREENS = {
  SOURCE_ID_PAID: 'payment-status-paid',
  PENDING: 'payment-status-pending',
  SUBMITTED: 'payment-status-succeeded',
  SUCCEEDED: 'payment-status-succeeded',
  FAILED: 'payment-status-failed',
  OPEN: 'payment-status-open',
  CANCELED: 'payment-status-canceled'
};
const getPaymentMethodTypes = (paymentMethods, paymentAmount, maximumAmounts) => {
  if (paymentMethods.length === 1) {
    return [hsToStripePaymentMethod(paymentMethods[0])];
  }
  const validPaymentMethods = maximumAmounts && getValidPaymentMethods(paymentMethods, paymentAmount, maximumAmounts);
  return validPaymentMethods;
};
export const getStripeOptions = (checkoutSession, paymentMethods, isZeroCheckout, theme, minimumAmount, maximumAmounts) => {
  const processorType = checkoutSession.get('processorType');
  const connectedAccountId = checkoutSession.get('connectedAccountId');
  const subtotalsPublicResponse = checkoutSession.get('subtotalsPublicResponse');
  const collectPaymentMethodOnFile = checkoutSession.get('collectPaymentMethodOnFile');
  const recurringSubtotals = subtotalsPublicResponse.get('recurringSubtotals');
  const paymentAmountInSmallestCurrencyUnit = subtotalsPublicResponse.get('paymentAmountInSmallestCurrencyUnit');
  const paymentAmount = subtotalsPublicResponse.get('paymentAmount');
  const hasRecurringSubtotals = recurringSubtotals && recurringSubtotals.size > 0;
  const getAmount = () => {
    const maxAmount = paymentMethods.length > 0 && !!maximumAmounts ? Math.max(...paymentMethods.filter(method => maximumAmounts[method] !== undefined).map(method => maximumAmounts[method])) : null;
    const isPaymentAmountLessThanOrEqualToMaxAmount = maxAmount && paymentAmount <= maxAmount;
    const isPaymentAmountGreaterThanOrEqualToMinimumAmount = minimumAmount && paymentAmount >= minimumAmount;
    if (isPaymentAmountGreaterThanOrEqualToMinimumAmount && isPaymentAmountLessThanOrEqualToMaxAmount) {
      return paymentAmountInSmallestCurrencyUnit;
    } else {
      return null;
    }
  };
  return {
    loader: AUTO,
    locale: getLangLocale(),
    onBehalfOf: processorType === HS_PAYMENTS ? connectedAccountId : null,
    setupFutureUsage: hasRecurringSubtotals || collectPaymentMethodOnFile ? OFF_SESSION : undefined,
    mode: isZeroCheckout ? SETUP : PAYMENT,
    amount: getAmount(),
    currency: checkoutSession.get('currencyCode').toLowerCase(),
    paymentMethodCreation: 'manual',
    paymentMethodTypes: getPaymentMethodTypes(paymentMethods, paymentAmount, maximumAmounts),
    paymentMethodOptions: {
      us_bank_account: {
        verification_method: FC_VERIFICATION_METHOD
      }
    },
    fonts: ELEMENTS_FONTS,
    appearance: ELEMENTS_APPEARANCE(theme)
  };
};
const CheckoutContainer = ({
  checkoutSession,
  trackPageView
}) => {
  const {
    pathname
  } = useLocation();
  const {
    formState
  } = usePaymentFormContext();
  const theme = useTheme();
  const paymentMethods = checkoutSession.get('paymentMethods').toJS();
  const acceptedPaymentMethods = paymentMethods.map(({
    paymentMethod
  }) => paymentMethod);
  const orderedPaymentMethods = useMemo(() =>
  // intersection to order acceptedPaymentMethods consistently
  intersection([CARD, ACH, BACS, PADS, SEPA], acceptedPaymentMethods), [acceptedPaymentMethods]);
  const {
    limits,
    loading: loadingTransactionLimits
  } = useTransactionLimits(checkoutSession.get('portalId'), checkoutSession.get('currencyCode'));
  const minimumAmount = limits && limits.minimumAmountForPaymentMethod[orderedPaymentMethods[0]];
  const maximumAmounts = limits && limits.maximumAmountForPaymentMethod;
  const isIframePreviewUrl = getIsIframePreviewUrl(pathname);
  const isIframePreviewTestUrl = getIsIframePreviewTestUrl(pathname);
  const iFrameLayout = getIframeLayout();
  const isCompactEmbed = iFrameLayout === LAYOUTS.EMBED;
  const isFullEmbed = iFrameLayout === LAYOUTS.EMBED_FULL;
  const publishableApiKey = checkoutSession.get('publishableApiKey');
  const connectedAccountId = checkoutSession.get('connectedAccountId');
  const processorType = checkoutSession.get('processorType');
  const isBYOSPortal = processorType === BYO_STRIPE;
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.getIn(['subtotalsPublicResponse', 'paymentAmount']), checkoutSession.get('lineItems').toJS());
  const status = checkoutSession.get('status');
  const isLiveMode = checkoutSession.get('liveMode');
  const isPreview = isIframePreviewUrl || isIframePreviewTestUrl;
  const shouldShowTestModeAlert = !isLiveMode && (!isIframePreviewUrl || isIframePreviewTestUrl) && status === OPEN;
  useEscapeListener(checkoutSession.get('sourceId'));
  useEffect(() => {
    const companyName = checkoutSession.getIn(['userDefinedBranding', 'companyName']);
    if (companyName) {
      document.title = unescapedText('payments.checkoutTitle', {
        companyName
      });
    } else {
      document.title = unescapedText('payments.checkoutHeader');
    }
    initializeSubscriptionTracker(checkoutSession.get('portalId'));
    const sourceUrl = checkoutSession.get('sourceUrl');
    const supportEmail = checkoutSession.getIn(['companyDetails', 'supportEmail']);
    initializePaymentsTracker({
      portalId: checkoutSession.get('portalId'),
      sourceUrl,
      workflow: 'checkout',
      merchantSupportEmail: supportEmail
    });
    if (trackPageView && status in STATUS_SUBSCREENS) {
      const numLineItemsOpenAmount = checkoutSession.get('lineItems').filter(lineItem => !!lineItem.getIn(['customConfiguration', 'hasEditablePrice'])).size;
      const numLineItemsWithTax = checkoutSession.get('lineItems').filter(lineItem => !!lineItem.get('tax')).size;
      const paymentScheduleEnabled = checkoutSession.get('lineItems').some(lineItem => {
        const sourceId = lineItem.get('sourceId');
        return !!sourceId && sourceId.includes(PAYMENT_SCHEDULE_MARKER);
      });
      track('pageView', {
        subscreen: STATUS_SUBSCREENS[status],
        paymentSessionId: checkoutSession.get('id'),
        interactionSections: getPageInteractionSections(checkoutSession),
        numContactFormFields: checkoutSession.get('formFields').size,
        numLineItemsEditable: checkoutSession.get('lineItems').filter(lineItem => !!lineItem.getIn(['customConfiguration', 'hasEditableQuantity'])).size,
        paymentScheduleEnabled,
        requiredCheckout: !checkoutSession.get('lineItems').some(lineItem => !!lineItem.getIn(['customConfiguration', 'isOptional'])),
        numLineItemsOpenAmount,
        numLineItemsWithTax,
        dealPipelineStageId: numLineItemsOpenAmount,
        discountShown: !!checkoutSession.get('isDiscountCodeEnabled'),
        sourceApp: checkoutSession.get('sourceApp'),
        paymentType: checkoutSession.get('processorType'),
        isPaymentMethodOnFileEnabled: checkoutSession.get('collectPaymentMethodOnFile'),
        salesTax: getSalesTaxType(checkoutSession)
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mounts a fire alarm for this app if one is active
  useFireAlarm(FIRE_ALARM_CONTAINER_ID);
  const stripeOptions = getStripeOptions(checkoutSession, orderedPaymentMethods, isZeroCheckout, theme, minimumAmount, maximumAmounts);
  if (loadingTransactionLimits) return null;
  if (status === CANCELED) {
    return /*#__PURE__*/_jsx(PaymentStatus, {
      checkoutSession: checkoutSession.toJS()
    });
  }
  if (formState.get('pageError')) {
    return /*#__PURE__*/_jsx(FullPageError, {});
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(View, {
      id: FIRE_ALARM_CONTAINER_ID,
      marginBlockEnd: "400",
      as: "div"
    }), shouldShowTestModeAlert && /*#__PURE__*/_jsx(View, {
      marginBlockEnd: "600",
      marginBlockStart: "100",
      as: "div",
      children: /*#__PURE__*/_jsx(TestModeAlert, {
        checkoutSession: checkoutSession.toJS()
      })
    }), /*#__PURE__*/_jsx(Elements, {
      stripe: getStripePromise(publishableApiKey, isBYOSPortal, connectedAccountId, isPreview),
      options: stripeOptions,
      children: /*#__PURE__*/_jsxs(Grid, {
        "data-test-id": "checkout-container",
        children: [!isCompactEmbed && /*#__PURE__*/_jsxs(GridItem, {
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: isFullEmbed ? 12 : 6
          },
          children: [/*#__PURE__*/_jsx(OrderSummary, {
            checkoutSession: checkoutSession.toJS()
          }), status === OPEN && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(MeetingTime, {}), /*#__PURE__*/_jsx(OrderDescription, {
              checkoutSession: checkoutSession.toJS()
            })]
          })]
        }), /*#__PURE__*/_jsx(GridItem, {
          size: {
            xs: 12,
            sm: 12,
            md: 12,
            lg: isFullEmbed || isCompactEmbed ? 12 : 6
          },
          children: status === OPEN ? /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(PaymentContainer, {
              checkoutSession: checkoutSession
            }), /*#__PURE__*/_jsx(HubSpotTermsOfServiceFooter, {})]
          }) : /*#__PURE__*/_jsx(PaymentStatus, {
            checkoutSession: checkoutSession.toJS()
          })
        })]
      })
    })]
  });
};
export default CheckoutContainer;